import Modal from '../../Unknown/Modal'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import { Box, Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import translate from '../../../i18n/translate'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import { FIELDS, useValidationSchema } from './useValidationSchema'
import { eventApi } from '../../../modules/services/eventService'

type EventCrossQRScannerModalProps = {
  event: {
   id: number; 
  }
}

export default function EventCrossQRScannerModal({
  event,
}: EventCrossQRScannerModalProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { toggleEventGiftTicketsModal } = modalsSlice.actions
  const visible = useAppSelector(state => state.modalsReducer.eventGiftTickets)

  const { data, isLoading } = eventApi.useGetEventTicketListQuery({ eventId: event?.id }, { skip: !event?.id, refetchOnMountOrArgChange: true })
  const [giftTickets, { isLoading: isUpdateLoading }] = eventApi.useGiftEventTicketsMutation()
  const handleSubmit = async (values: any) => {
    try {
      const params = {
        eventId: event?.id,
        users: values?.[FIELDS.USERS],
        smsText: values?.[FIELDS.SMS_TEXT],
        ticketId: values?.[FIELDS.TICKET_ID] ? values?.[FIELDS.TICKET_ID] : null,
        ticketName: values?.[FIELDS.TICKET_NAME] ? values?.[FIELDS.TICKET_NAME] : null,
      }
      const { data: resData }: any = await giftTickets(params);

      if (resData?.message) {
        toast.error(resData?.message)
      } else {
        dispatch(toggleEventGiftTicketsModal())
        toast.success(translate('ticketsSuccessGifted'))
      }
    } catch (error) {}
  }

  const onCloseModal = () => {
    dispatch(toggleEventGiftTicketsModal())
  }

  const validationSchema = useValidationSchema()

  return (
    <Modal
      title={translate('eventGiftTicketsModalTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '500px' }}
      setVisible={onCloseModal}
    >
      <Formik
        initialValues={{
          [FIELDS.USERS]: '',
          [FIELDS.SMS_TEXT]: '',
        }}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({ errors, values, touched, handleBlur, handleChange, isValid }) => {
          return (
            <Form>
              <Box>
                <Box display='flex' flexDirection='column' padding={2} gap={1.5}>
                  <Box display='flex' flexDirection='column' gap={1}>
                    <Typography>
                      {intl.formatMessage({ id: 'giftUsersHelper' })}
                    </Typography>
                    <TextField
                      name={FIELDS.USERS}
                      value={values?.[FIELDS.USERS]}
                      error={touched?.[FIELDS.USERS] && Boolean(errors?.[FIELDS.USERS])}
                       helperText={
                        touched[FIELDS.USERS] && errors[FIELDS.USERS]
                          ? errors?.[FIELDS.USERS]?.split("\n").map((line, i) => (
                              <div key={i}>{line}</div>
                            ))
                          : null
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant='outlined'
                      multiline
                      rows={5}
                      color='warning'
                      disabled={isLoading}
                      fullWidth
                      label={intl.formatMessage({
                        id: 'giftUsers'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'giftUsers'
                      })}
                      dir='ltr'
                      sx={{
                        '.MuiInputBase-root.MuiInput-root:before': {
                          borderBottom: '0px'
                        },
                        '.MuiInputBase-root.MuiInput-root:hover': {
                          borderBottom: '0px'
                        }
                      }}
                    />
                  </Box>
                  <Box display='flex' flexDirection='column' gap={1}>
                    <Typography>
                      {intl.formatMessage({ id: 'giftSmsTextHelper' })}
                    </Typography>
                    <TextField
                      name={FIELDS.SMS_TEXT}
                      value={values?.[FIELDS.SMS_TEXT]}
                      error={touched?.[FIELDS.SMS_TEXT] && Boolean(errors?.[FIELDS.SMS_TEXT])}
                      helperText={touched?.[FIELDS.SMS_TEXT] && errors?.[FIELDS.SMS_TEXT]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant='outlined'
                      multiline
                      rows={3}
                      color='warning'
                      disabled={isLoading}
                      fullWidth
                      label={intl.formatMessage({
                        id: 'giftSmsText'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'giftSmsText'
                      })}
                      dir='ltr'
                      sx={{
                        '.MuiInputBase-root.MuiInput-root:before': {
                          borderBottom: '0px'
                        },
                        '.MuiInputBase-root.MuiInput-root:hover': {
                          borderBottom: '0px'
                        }
                      }}
                    />
                  </Box>
                  <Box display='flex' flexDirection='column' gap={1}>
                    <Typography>
                      {intl.formatMessage({ id: 'giftTicketHelper' })}
                    </Typography>
                    <FormControl fullWidth error={touched?.[FIELDS.TICKET_ID] && Boolean(errors?.[FIELDS.TICKET_ID])}>
                      <InputLabel color='warning' id="ticket-id-label">
                        {intl.formatMessage({ id: 'giftTicketSelect' })}
                      </InputLabel>
                      <Select
                        color='warning'
                        label={intl.formatMessage({ id: 'giftTicketSelect' })}
                        id="ticket-id-label"
                        name={FIELDS.TICKET_ID}
                        value={values?.[FIELDS.TICKET_ID]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <MenuItem value="">{intl.formatMessage({ id: 'giftNoneOption' })}</MenuItem>
                        {data?.map?.((item: any) => (
                          <MenuItem key={item.id} value={item.id}>{`${item.name} (₪ ${item.currentPrice} / ${item.currentCount})`}</MenuItem>
                        ))}
                      </Select>
                      {touched?.[FIELDS.TICKET_ID] && Boolean(errors?.[FIELDS.TICKET_ID]) && (
                        <FormHelperText>{errors?.[FIELDS.TICKET_ID]}</FormHelperText>
                      )}
                    </FormControl>
                    <TextField
                      name={FIELDS.TICKET_NAME}
                      value={values?.[FIELDS.TICKET_NAME]}
                      error={touched?.[FIELDS.TICKET_NAME] && Boolean(errors?.[FIELDS.TICKET_NAME])}
                      helperText={touched?.[FIELDS.TICKET_NAME] && errors?.[FIELDS.TICKET_NAME]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant='outlined'
                      color='warning'
                      disabled={isLoading}
                      fullWidth
                      label={intl.formatMessage({
                        id: 'giftTicketName'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'giftTicketName'
                      })}
                      dir='ltr'
                      sx={{
                        '.MuiInputBase-root.MuiInput-root:before': {
                          borderBottom: '0px'
                        },
                        '.MuiInputBase-root.MuiInput-root:hover': {
                          borderBottom: '0px'
                        }
                      }}
                    />
                  </Box>
                </Box>
                <Button
                  size='large'
                  variant='contained'
                  color='warning'
                  type='submit'
                  fullWidth
                  disabled={isLoading || !isValid || isUpdateLoading}
                >
                  {translate('giftTickets')}
                </Button>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
